import type { TaskRun, TaskRunPresentation } from "../../../shared/types";

const DEFAULT_PRESENTATION: TaskRunPresentation = {
  job_label: "Marketing run",
  brief_label: "Your brief",
  result_label: "Research report",
  evidence_label: "Supporting findings",
};

function safeLabel(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().replace(/[\u0000-\u001F\u007F]/g, " ").replace(/\s+/g, " ");
  return normalized && normalized.length <= 120 ? normalized : null;
}

/** Client fallback for stale/mock API rows; production API owns the canonical projection. */
export function legacyRunJobLabel(taskSlug: string): string {
  const normalized = taskSlug.trim();
  if (!normalized || normalized.length > 120 || /[\u0000-\u001F\u007F]/.test(normalized)) return "Marketing run";
  const [baseSlug, ...scope] = normalized.split(":");
  if (scope.length > 0 && baseSlug !== "content_draft") return "Marketing run";
  return (baseSlug ?? "").replace(/[_-]+/g, " ").replace(/\b\w/g, (character) => character.toUpperCase()) || "Marketing run";
}

/** The only display resolver for a task run in marketer-facing workspace surfaces. */
export function taskRunPresentation(run: Pick<TaskRun, "task_slug" | "presentation">): TaskRunPresentation {
  const presentation = run.presentation;
  const resultView = presentation?.result_view;
  const recoveryKinds = presentation?.recovery_kinds;
  return {
    job_label: safeLabel(presentation?.job_label) ?? legacyRunJobLabel(run.task_slug),
    brief_label: safeLabel(presentation?.brief_label) ?? DEFAULT_PRESENTATION.brief_label,
    result_label: safeLabel(presentation?.result_label) ?? DEFAULT_PRESENTATION.result_label,
    evidence_label: safeLabel(presentation?.evidence_label) ?? DEFAULT_PRESENTATION.evidence_label,
    result_view: resultView === "market_research_review" || resultView === "current_events_campaign_research" || resultView === "social_listening_action" || resultView === "standard"
      ? resultView
      : undefined,
    recovery_kinds: Array.isArray(recoveryKinds)
      ? recoveryKinds.filter((kind): kind is "none" | "retry" | "rerun" | "link" => kind === "none" || kind === "retry" || kind === "rerun" || kind === "link")
      : undefined,
  };
}

/**
 * A persisted Action contract owns specialized result UI. Social-listening
 * Runs created before that contract existed retain their historical renderer,
 * but malformed snapshots deliberately never receive a live or legacy view.
 */
export function taskRunUsesResultView(
  run: Pick<TaskRun, "task_slug" | "presentation">,
  resultView: NonNullable<TaskRunPresentation["result_view"]>,
): boolean {
  if (taskRunPresentation(run).result_view === resultView) return true;
  return resultView === "social_listening_action"
    && run.presentation?.result_view === undefined
    && run.presentation?.compatibility_state === undefined
    && run.task_slug === "social_listening";
}

export function taskRunJobLabel(run: Pick<TaskRun, "task_slug" | "presentation">): string {
  return taskRunPresentation(run).job_label;
}
